<template>
  <div>
    <el-alert
      type="info"
      :closable="false"
      class="mb-3"
      show-icon
    >
      <template #title>
        Configure para cada proceso en qué pantallas se mostrará y editará este atributo
      </template>
    </el-alert>

    <div class="grilla-unificada-wrapper">
      <el-table
        v-loading="loading"
        :data="filtrados"
        border
        class="grilla-unificada"
      >
        <el-table-column
          prop="Descripcion"
          min-width="200"
          fixed
          align="left"
        >
          <template #header>
            <div style="display: flex; flex-direction: column; gap: 8px">
              <span style="font-weight: 600">Proceso</span>
              <el-input
                v-model="filtro"
                size="small"
                placeholder="Buscar..."
                clearable
              />
            </div>
          </template>
        </el-table-column>

        <!-- Grupo de columnas por cada módulo -->
        <el-table-column
          v-for="modulo in modulos"
          :key="modulo.valor"
          :label="modulo.nombre"
          align="center"
        >
          <!-- Sub-columna: Visible -->
          <el-table-column
            label="Visible"
            width="80"
            align="center"
          >
            <template #header>
              <div class="columna-header">
                <span>Visible</span>
                <el-checkbox
                  :model-value="todosVisibles(modulo.valor)"
                  :indeterminate="algunosVisibles(modulo.valor)"
                  class="check-header"
                  @change="toggleTodosVisibles(modulo.valor, $event)"
                />
              </div>
            </template>
            <template #default="{ row }">
              <el-checkbox
                :model-value="esVisible(row, modulo.valor)"
                @change="toggleVisible(row, modulo.valor, $event)"
              />
            </template>
          </el-table-column>

          <!-- Sub-columna: Editable -->
          <el-table-column
            label="Editable"
            width="80"
            align="center"
          >
            <template #header>
              <div class="columna-header">
                <span>Editable</span>
                <el-checkbox
                  :model-value="todosEditables(modulo.valor)"
                  :indeterminate="algunosEditables(modulo.valor)"
                  class="check-header"
                  @change="toggleTodosEditables(modulo.valor, $event)"
                />
              </div>
            </template>
            <template #default="{ row }">
              <el-checkbox
                :model-value="esEditable(row, modulo.valor)"
                @change="toggleEditable(row, modulo.valor, $event)"
              />
            </template>
          </el-table-column>
        </el-table-column>
      </el-table>
    </div>

    <div class="mt-3 text-center">
      <el-button
        type="primary"
        :loading="saving"
        @click="$emit('guardar')"
      >
        Guardar Cambios
      </el-button>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
} from 'vue';

const props = defineProps({
  procesos: {
    type: Array,
    required: true,
  },
  modulos: {
    type: Array,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  saving: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['guardar']);

const filtro = ref('');

const filtrados = computed(() => {
  if (!filtro.value.trim()) {
    return props.procesos;
  }
  const f = filtro.value.toLowerCase();
  return props.procesos.filter((proc) => proc.Descripcion.toLowerCase().includes(f));
});

// Funciones de bitfield
function esVisible(row, valorModulo) {
  return (row.ModuloVisible & valorModulo) !== 0;
}

function esEditable(row, valorModulo) {
  return (row.ModuloEditable & valorModulo) !== 0;
}

function toggleVisible(row, valorModulo, activar) {
  if (activar) {
    row.ModuloVisible |= valorModulo;
  } else {
    row.ModuloVisible &= ~valorModulo;
    row.ModuloEditable &= ~valorModulo;
  }
}

function toggleEditable(row, valorModulo, activar) {
  if (activar) {
    row.ModuloEditable |= valorModulo;
    row.ModuloVisible |= valorModulo;
  } else {
    row.ModuloEditable &= ~valorModulo;
  }
}

function todosVisibles(valorModulo) {
  return (
    props.procesos.length > 0 &&
    props.procesos.every((proc) => (proc.ModuloVisible & valorModulo) !== 0)
  );
}

function algunosVisibles(valorModulo) {
  const count = props.procesos.filter((proc) => (proc.ModuloVisible & valorModulo) !== 0).length;
  return count > 0 && count < props.procesos.length;
}

function toggleTodosVisibles(valorModulo, activar) {
  props.procesos.forEach((row) => {
    toggleVisible(row, valorModulo, activar);
  });
}

function todosEditables(valorModulo) {
  return (
    props.procesos.length > 0 &&
    props.procesos.every((proc) => (proc.ModuloEditable & valorModulo) !== 0)
  );
}

function algunosEditables(valorModulo) {
  const count = props.procesos.filter((proc) => (proc.ModuloEditable & valorModulo) !== 0).length;
  return count > 0 && count < props.procesos.length;
}

function toggleTodosEditables(valorModulo, activar) {
  props.procesos.forEach((row) => {
    toggleEditable(row, valorModulo, activar);
  });
}
</script>

<style scoped>
.mb-3 {
  margin-bottom: 12px;
}

.mt-3 {
  margin-top: 12px;
}

.grilla-unificada-wrapper {
  width: 100%;
  position: relative;
  margin-bottom: 20px;
}

.grilla-unificada {
  min-width: 820px;
}

.grilla-unificada :deep(.el-table__body-wrapper) {
  max-height: 45vh;
  overflow-y: auto;
}

.grilla-unificada :deep(.el-table__header-wrapper) {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #fff;
}

.grilla-unificada :deep(.el-table__header) {
  background-color: #f5f7fa;
}

.columna-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 4px 0;
}

.columna-header span {
  font-size: 12px;
  font-weight: normal;
}

.check-header {
  margin-top: 4px;
}

.grilla-unificada :deep(.el-table__body .cell) {
  display: flex;
  align-items: center;
}

.grilla-unificada :deep(.el-table__body .el-table__cell:not(:first-child) .cell) {
  justify-content: center;
}

.grilla-unificada :deep(.el-table__body .el-table__cell:first-child .cell) {
  justify-content: flex-start;
  padding-left: 12px;
}
</style>
